import { useState } from 'react';
import type { SidebarNavItem, SidebarNavGroup, SidebarNavSingle } from '@/mocks/achievement-detail-sections';
import { RailwayTrackBorder } from '@/components/feature/RailwayPatterns';

interface DetailSidebarProps {
  items: SidebarNavItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  tabLabel?: string;
  hideGroupHeader?: boolean;
  className?: string;
  onClose?: () => void;
}

/* ── Icon mapping for each sidebar item by id ── */
const itemIconMap: Record<string, string> = {
  // Video
  'vid-overview': 'ri-film-line',
  'vid-teaching': 'ri-slideshow-line',
  'vid-innovation': 'ri-lightbulb-flash-line',
  'vid-enterprise': 'ri-building-2-line',
  'vid-student': 'ri-graduation-cap-line',
  // Application
  'app-form': 'ri-file-list-3-line',
  'app-awards': 'ri-medal-line',
  'app-timeline': 'ri-calendar-line',
  // Materials
  'mat-honors': 'ri-award-line',
  'mat-curriculum': 'ri-book-open-line',
  'mat-textbooks': 'ri-stack-line',
  'mat-platform': 'ri-server-line',
  'mat-faculty': 'ri-team-line',
  'mat-party': 'ri-flag-line',
  'mat-reform-projects': 'ri-rocket-line',
  'mat-industry-cooperation': 'ri-exchange-line',
  'mat-international': 'ri-earth-line',
  'mat-excellence': 'ri-star-smile-line',
  'mat-student-awards': 'ri-trophy-line',
  'mat-student-innovation': 'ri-lightbulb-line',
  'mat-outstanding-graduates': 'ri-star-line',
  'mat-enterprise-evaluation': 'ri-feedback-line',
  'mat-university-evaluation': 'ri-building-2-line',
  'mat-social-impact': 'ri-newspaper-line',
  // Report
  'rep-background': 'ri-history-line',
  'rep-approach': 'ri-route-line',
  'rep-content': 'ri-file-list-line',
  'rep-innovation': 'ri-flashlight-line',
  'rep-promotion': 'ri-global-line',
  // Singles
  'section-intro': 'ri-information-line',
  'section-methods': 'ri-tools-line',
  'section-innovation': 'ri-sparkling-line',
  'section-promotion': 'ri-share-circle-line',
  'section-contributors': 'ri-user-settings-line',
  'section-units': 'ri-building-line',
  'section-recommend': 'ri-thumb-up-line',
};

function getItemIcon(id: string): string {
  return itemIconMap[id] || 'ri-file-line';
}

function getItemNumber(num: number): string {
  return String(num);
}

/* ── Decorative slanted corner clip mask effect via CSS ── */
function SlantedCard({
  children,
  isActive,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  children: React.ReactNode;
  isActive: boolean;
  isHovered: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`w-full text-left transition-all duration-300 cursor-pointer overflow-hidden relative ${
        isActive
          ? 'bg-primary-500 text-white'
          : isHovered
            ? 'bg-primary-50 text-primary-800'
            : 'bg-white text-foreground-700 hover:bg-background-100'
      }`}
      style={{
        borderRadius: '16px 6px 16px 6px',
        boxShadow: isActive
          ? '0 4px 14px rgba(218,41,28,0.22)'
          : isHovered
            ? '0 2px 8px rgba(218,41,28,0.10)'
            : '0 1px 3px rgba(0,0,0,0.04)',
        transform: isActive ? 'translateX(6px)' : isHovered ? 'translateX(3px)' : 'translateX(0)',
      }}
    >
      {/* Left accent bar — visible on active */}
      <span
        className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full transition-all duration-300 ${
          isActive ? 'bg-white/60' : 'bg-transparent'
        }`}
      />
      {/* Top-right decorative slant */}
      <span
        className={`absolute top-0 right-0 w-12 h-12 transition-all duration-300 ${
          isActive ? 'bg-white/10' : 'bg-primary-500/5'
        }`}
        style={{
          clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
          borderRadius: '0 6px 0 0',
        }}
      />
      {children}
    </button>
  );
}

/* ── Group Header ── */
function GroupHeader({
  group,
  startNum,
  activeId,
  onSelect,
}: {
  group: SidebarNavGroup;
  startNum: number;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const isGroupActive = group.items.some((item) => item.id === activeId);

  return (
    <div className="mb-2">
      {/* Group title bar — static, no toggle */}
      <div
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left rounded-xl transition-all duration-300 ${
          isGroupActive
            ? 'bg-primary-500/10 text-primary-700'
            : 'bg-background-100 text-foreground-500'
        }`}
        style={{
          borderRadius: '12px 4px 12px 4px',
        }}
      >
        <div className={`w-7 h-7 flex items-center justify-center rounded-md transition-all duration-250 ${
          isGroupActive ? 'bg-primary-500 text-white' : 'bg-white text-foreground-400'
        }`}>
          <i className="ri-folder-open-line text-sm" />
        </div>
        <span className="text-[16px] font-bold font-heading tracking-wide">{group.title}</span>
      </div>

      {/* Group Items — always visible */}
      <div className="flex flex-col gap-2 mt-2 pl-2">
        {group.items.map((item, idx) => {
          const isActive = activeId === item.id;
          const isHovered = hoveredItem === item.id;
          const icon = getItemIcon(item.id);
          const num = getItemNumber(startNum + idx);
          return (
            <SlantedCard
              key={item.id}
              isActive={isActive}
              isHovered={isHovered}
              onClick={() => onSelect(item.id)}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div className="flex items-center gap-3 px-4 py-4 min-h-[72px]">
                {/* Number badge */}
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 font-heading font-bold text-[12px] transition-all duration-300 ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-primary-500/10 text-primary-500'
                }`}>
                  {num}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      isActive ? 'text-white/90' : 'text-foreground-400'
                    }`}>
                      <i className={`${icon} text-[16px]`} />
                    </div>
                    <span className={`text-[16px] font-semibold truncate leading-tight ${
                      isActive ? 'text-white' : 'text-foreground-700'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                </div>
                {/* Arrow */}
                <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-all duration-300 ${
                  isActive
                    ? 'opacity-100 text-white/80'
                    : 'opacity-0 group-hover:opacity-50'
                }`}>
                  <i className="ri-arrow-right-s-line text-sm" />
                </div>
              </div>
            </SlantedCard>
          );
        })}
      </div>
    </div>
  );
}

/* ── Single Item ── */
function SingleItem({
  item,
  activeId,
  onSelect,
  index,
}: {
  item: SidebarNavSingle;
  activeId: string | null;
  onSelect: (id: string) => void;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isActive = activeId === item.id;
  const icon = getItemIcon(item.id);
  const num = String(index);

  return (
    <SlantedCard
      isActive={isActive}
      isHovered={isHovered}
      onClick={() => onSelect(item.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-3 px-4 py-4 min-h-[72px]">
        <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 font-heading font-bold text-[12px] transition-all duration-300 ${
          isActive
            ? 'bg-white/20 text-white'
            : 'bg-primary-500/10 text-primary-500'
        }`}>
          {num}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-colors duration-300 ${
              isActive ? 'text-white/90' : 'text-foreground-400'
            }`}>
              <i className={`${icon} text-[16px]`} />
            </div>
            <span className={`text-[16px] font-semibold truncate leading-tight ${
              isActive ? 'text-white' : 'text-foreground-700'
            }`}>
              {item.title}
            </span>
          </div>
        </div>
        <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-all duration-300 ${
          isActive ? 'opacity-100 text-white/80' : 'opacity-0'
        }`}>
          <i className="ri-arrow-right-s-line text-sm" />
        </div>
      </div>
    </SlantedCard>
  );
}

export default function DetailSidebar({ items, activeId, onSelect, tabLabel, hideGroupHeader, className, onClose }: DetailSidebarProps) {
  let itemNumber = 1;

  return (
    <aside className={`w-[300px] shrink-0 border-r border-background-200 bg-background-50 overflow-y-auto h-full min-h-0 relative ${className || ''}`}>
      {/* Mobile close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="lg:hidden absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-background-300 hover:text-foreground-700 transition-colors cursor-pointer"
          aria-label="关闭侧边栏"
        >
          <i className="ri-close-line text-lg" />
        </button>
      )}
      {/* Railway track border at top */}
      <RailwayTrackBorder />

      {/* Navigation Items */}
      <nav className="py-4 px-3 relative z-[1] flex flex-col gap-3">
        {items.map((item) => {
          if (item.type === 'group') {
            const startNum = itemNumber;
            itemNumber += item.items.length;
            // When hideGroupHeader is true, render only the items without the group header
            if (hideGroupHeader) {
              return item.items.map((subItem, idx) => {
                const num = getItemNumber(startNum + idx);
                const icon = getItemIcon(subItem.id);
                const isActive = activeId === subItem.id;
                return (
                  <SlantedCard
                    key={subItem.id}
                    isActive={isActive}
                    isHovered={false}
                    onClick={() => onSelect(subItem.id)}
                    onMouseEnter={() => {}}
                    onMouseLeave={() => {}}
                  >
                    <div className="flex items-center gap-3 px-4 py-4 min-h-[72px]">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 font-heading font-bold text-[12px] transition-all duration-300 ${
                        isActive
                          ? 'bg-white/20 text-white'
                          : 'bg-primary-500/10 text-primary-500'
                      }`}>
                        {num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-colors duration-300 ${
                            isActive ? 'text-white/90' : 'text-foreground-400'
                          }`}>
                            <i className={`${icon} text-[16px]`} />
                          </div>
                          <span className={`text-[16px] font-semibold truncate leading-tight ${
                            isActive ? 'text-white' : 'text-foreground-700'
                          }`}>
                            {subItem.label}
                          </span>
                        </div>
                      </div>
                      <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isActive ? 'opacity-100 text-white/80' : 'opacity-0'
                      }`}>
                        <i className="ri-arrow-right-s-line text-sm" />
                      </div>
                    </div>
                  </SlantedCard>
                );
              });
            }
            return (
              <GroupHeader
                key={item.id}
                group={item}
                startNum={startNum}
                activeId={activeId}
                onSelect={onSelect}
              />
            );
          }
          const currentNum = itemNumber;
          itemNumber += 1;
          return (
            <SingleItem
              key={item.id}
              item={item}
              activeId={activeId}
              onSelect={onSelect}
              index={currentNum}
            />
          );
        })}
      </nav>
    </aside>
  );
}